import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../css/SurgePage.css";
import {
    fetchAllSurges,
    createSurge,
    activateSurge,
    deactivateAllSurges,
    updateSurge
} from "../../service/api/adminApi";
import {useNavigate} from "react-router-dom";


const IconPlus = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const IconX = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IconShield = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);
const IconEye = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
);

const Toast = ({ message, type, onDone }) => {
    useEffect(() => {
        const t = setTimeout(onDone, 3000);
        return () => clearTimeout(t);
    }, [onDone]);
    return <div className={`surge-toast ${type}`}>{type === "success" ? "✓" : "✗"} {message}</div>;
};

const SurgeToggle = ({ checked, onChange, disabled }) => (
    <label className="surge-toggle" title={checked ? "Active — click to deactivate all" : "Inactive — click to activate"}>
        <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
        <span className="surge-toggle-track" />
        <span className="surge-toggle-thumb" />
    </label>
);

const SkeletonRows = () => (
    <>
        {[1, 2, 3].map(i => (
            <tr key={i} className="surge-skeleton-row">
                {[80, 140, 70, 90, 120, 100, 60, 100].map((w, j) => (
                    <td key={j}><div className="surge-skeleton-cell" style={{ width: w }} /></td>
                ))}
            </tr>
        ))}
    </>
);

const EMPTY_FORM = { name: "", description: "", payment_type: "fixed", payment: "", partner_amount: "" };

const SurgeModal = ({ mode, initial, onClose, onSave }) => {
    const [form, setForm] = useState(initial ?? EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const firstRef = useRef(null);

    const isEdit = mode === "edit";

    useEffect(() => { firstRef.current?.focus(); }, []);
    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.payment) return;
        setSaving(true);
        try { await onSave(form); } finally { setSaving(false); }
    };


    const handleEdit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.description.trim()) return;
        setSaving(true);
        try { await onSave(form); } finally { setSaving(false); }
    }



    return (
        <div className="surge-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="surge-modal">
                <div className="surge-modal-header">
                    <div className="surge-modal-title">{mode === "create" ? "New Surge Rule" : "Edit Surge Rule"}</div>
                    <button className="surge-modal-close" onClick={onClose}><IconX size={13} /></button>
                </div>
                <form onSubmit={isEdit ? handleEdit : handleSubmit}>
                    <div className="surge-modal-body">
                        <div className="surge-field">
                            <label className="surge-label">Surge Name *</label>
                            <input ref={firstRef} className="surge-input" value={form.name} onChange={set("name")} placeholder="e.g. Weekend Peak, Rain Surge" required />
                        </div>
                        <div className="surge-field">
                            <label className="surge-label">Description</label>
                            <textarea className="surge-textarea" value={form.description} onChange={set("description")} placeholder="Optional — explain when / why this surge applies." rows={2} />
                        </div>
                        {!isEdit && (
                            <div className="surge-row">
                                <div className="surge-field">
                                    <label className="surge-label">Charge Type *</label>
                                    <select className="surge-select" value={form.payment_type}
                                            onChange={set("payment_type")} required>
                                        <option value="fixed">₹ Fixed (flat fee)</option>
                                        <option value="percent">% Percent (of total)</option>
                                    </select>
                                    <span
                                        className="surge-hint">{form.payment_type === "percent" ? "Applied as % of order total" : "Flat fee added to every order"}</span>
                                </div>
                                <div className="surge-field">
                                    <label
                                        className="surge-label">{form.payment_type === "percent" ? "Percentage (0–100)" : "Amount (₹)"} *</label>
                                    <input className="surge-input" type="number" min="0"
                                           max={form.payment_type === "percent" ? 100 : undefined} step="0.01"
                                           value={form.payment} onChange={set("payment")}
                                           placeholder={form.payment_type === "percent" ? "e.g. 10" : "e.g. 40"} required/>
                                    <span
                                        className="surge-hint">{form.payment_type === "percent" ? "E.g. 10 = 10% surge fee" : "E.g. 40 = ₹40 flat surge"}</span>
                                </div>
                                <div className="surge-field">
                                    <label className="surge-label">Partner Amount Per Order (₹)</label>
                                    <input className="surge-input" type="number" min="0" step="0.01"
                                           value={form.partner_amount} onChange={set("partner_amount")}
                                           placeholder="e.g. 20"/>
                                    <span className="surge-hint">Amount paid to partner per order</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="surge-modal-footer">
                        <button type="button" className="surge-cancel-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="surge-submit-btn" disabled={saving}>
                            {saving ? "Saving…" : mode === "create" ? "Create Surge" : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const SurgePage = () => {
    const [surges, setSurges]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [modal, setModal]         = useState(null);
    const [toggling, setToggling]   = useState(null);
    const [toast, setToast]         = useState(null);
    const navigate = useNavigate();

    const activeSurge = surges.find(s => s.is_active) ?? null;


    const load = useCallback(async () => {
        setLoading(true);
        const data = await fetchAllSurges();
        if (data?.data) setSurges(data.data);
        else if (Array.isArray(data)) setSurges(data);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const notify = (message, type = "success") => setToast({ message, type });

    const handleToggle = async (surge) => {
        if (toggling) return;
        setToggling(surge.id);
        try {
            if (surge.is_active) {
                await deactivateAllSurges();
                notify("All surges deactivated.");
            } else {
                await activateSurge(surge.id);
                notify(`"${surge.name}" is now the active surge.`);
            }
            await load();
        } catch {
            notify("Failed to update surge status.", "error");
        } finally {
            setToggling(null);
        }
    };

    const handleDeactivateAll = async () => {
        if (toggling) return;
        setToggling("all");
        try {
            await deactivateAllSurges();
            notify("Surge pricing deactivated.");
            await load();
        } catch {
            notify("Failed to deactivate surges.", "error");
        } finally {
            setToggling(null);
        }
    };

    const handleCreate = async (form) => {
        try {
            await createSurge(form);
            notify("Surge rule created.");
            setModal(null);
            await load();
        } catch {
            notify("Failed to create surge.", "error");
            throw new Error("failed");
        }
    };


    const handleUpdate = async (form) => {
        try {
            await updateSurge({
                id: modal.data.id,
                name: form.name,
                description: form.description,
                is_active: modal.data.is_active, // keep existing state
            });

            notify("Surge updated successfully.");
            setModal(null);
            await load();
        } catch {
            notify("Failed to update surge.", "error");
            throw new Error("failed");
        }
    };

    const fmtValue = (s) =>
        s.payment_type === "percent"
            ? `${parseFloat(s.payment)}%`
            : `₹${parseFloat(s.payment).toFixed(2)}`;

    return (
        <div className="surge-page">
            <div className="surge-page-head">
                <div className="surge-topbar">
                    <div className="surge-page-title">Surge Management</div>
                    <button className="surge-create-btn" onClick={() => setModal({ mode: "create" })}>
                        <IconPlus size={15} /> New Surge Rule
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="surge-banner-skeleton"><span className="surge-spinner" /> Checking surge status…</div>
            ) : activeSurge ? (
                <div className="surge-active-banner">
                    <div className="surge-active-banner-left">
                        <span className="surge-active-banner-eyebrow">Active Surge</span>
                        <span className="surge-active-banner-name">{activeSurge.name}</span>
                        <span className="surge-active-banner-value">
                            {activeSurge.payment_type === "percent"
                                ? `${activeSurge.payment}% added to every order total`
                                : `₹${parseFloat(activeSurge.payment).toFixed(2)} flat fee on every order`}
                        </span>
                    </div>
                    <button className="surge-deactivate-btn" onClick={handleDeactivateAll} disabled={toggling === "all"}>
                        {toggling === "all" ? (<><span className="surge-spinner surge-spinner--white" />Deactivating…</>) : "Deactivate Surge"}
                    </button>
                </div>
            ) : (
                <div className="surge-inactive-banner">
                    <IconShield size={15} /> No active surge — standard pricing is in effect.
                </div>
            )}

            <div className="surge-section">
                <div className="surge-section-header">
                    <div className="surge-section-title">
                        <span className="surge-count-badge">{loading ? "–" : surges.length}</span>
                        Surge Rules
                    </div>
                </div>

                <div className="surge-table-wrap">
                    <table className="surge-table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Value</th>
                            <th>Partner Amount</th>
                            <th>Description</th>
                            <th>Active</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <SkeletonRows />
                        ) : surges.length === 0 ? (
                            <tr>
                                <td colSpan={8}>
                                    <div className="surge-empty-state">No surge rules yet. Create one to get started.</div>
                                </td>
                            </tr>
                        ) : (
                            surges.map(surge => (
                                <tr key={surge.id}>
                                    <td style={{ color: "#94a3b8", fontWeight: 600 }}>#{surge.id}</td>

                                    {/* Name — no description here anymore */}
                                    <td>
                                        <div className="surge-name-cell">{surge.name}</div>
                                    </td>

                                    <td>
                                            <span className={`surge-type-badge ${surge.payment_type}`}>
                                                {surge.payment_type === "percent" ? "% Percent" : "₹ Fixed"}
                                            </span>
                                    </td>

                                    <td><span className="surge-value-cell">{fmtValue(surge)}</span></td>

                                    <td><span className="surge-value-cell">₹{parseFloat(surge?.partner_amount || 0).toFixed(2)}</span></td>

                                    {/* Description — truncated with tooltip */}
                                    <td>
                                        {surge.description ? (
                                            <span
                                                className="surge-desc-truncated"
                                                title={surge.description}
                                            >
                                                    {surge.description}
                                                </span>
                                        ) : (
                                            <span style={{ opacity: 0.4 }}>—</span>
                                        )}
                                    </td>

                                    <td>
                                        <div className="surge-toggle-wrap">
                                            <SurgeToggle
                                                checked={!!surge.is_active}
                                                onChange={() => handleToggle(surge)}
                                                disabled={toggling === surge.id || toggling === "all"}
                                            />
                                            {toggling === surge.id ? (
                                                <span className="surge-spinner surge-spinner--sm" />
                                            ) : (
                                                <span className={`surge-toggle-label ${surge.is_active ? "active" : ""}`}>
                                                        {surge.is_active ? "Active" : "Off"}
                                                    </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* View button */}
                                    <td>
                                        <div style={{display: "flex", flexDirection: "row", gap: 5}}>
                                            <button
                                                className="surge-edit-btn"
                                                onClick={() => setModal({mode: "edit", data: surge})}
                                            >
                                                Edit
                                            </button>

                                            <button
                                                className="surge-view-btn"
                                                onClick={() => navigate(`/surges/${surge.id}/stats`)}
                                            >
                                                <IconEye size={13}/> View
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal?.mode === "create" && (
                <SurgeModal mode="create" onClose={() => setModal(null)} onSave={handleCreate}/>
            )}

            {modal?.mode === "edit" && (
                <SurgeModal
                    mode={modal.mode}
                    initial={
                        modal.mode === "edit"
                            ? {
                                name: modal.data.name,
                                description: modal.data.description,
                            }
                            : undefined
                    }
                    onClose={() => setModal(null)}
                    onSave={modal.mode === "edit" ? handleUpdate : handleCreate}
                />
            )}


            {toast && (
                <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />
            )}
        </div>
    );
};

export default SurgePage;